import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/', label: 'Dashboard', icon: '📊', end: true },
  { section: 'Pipeline' },
  { to: '/opportunities', label: 'Opportunities', icon: '💼' },
  { to: '/accounts', label: 'Accounts', icon: '🏢' },
  { to: '/contacts', label: 'Contacts', icon: '👤' },
  { section: 'Contracts' },
  { to: '/contracts', label: 'Contracts', icon: '📄' },
  { section: 'Delivery' },
  { to: '/staffing', label: 'Staffing', icon: '👥' },
  { section: 'Work' },
  { to: '/tasks', label: 'All Tasks', icon: '✅' },
  { to: '/activities', label: 'Activity Feed', icon: '💬' },
  { section: 'Insights' },
  { to: '/reports', label: 'Reports', icon: '📈' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

// Routes hidden from viewer-role users.
const VIEWER_BLOCKED_PATHS = new Set(['/', '/opportunities', '/contracts', '/staffing', '/reports']);

export function Sidebar() {
  const { user, logout } = useAuth();
  const isViewer = user?.role === 'viewer';
  // Filter the nav list for viewers, then drop any section header that ends up with no following links.
  const visibleNav = (() => {
    if (!isViewer) return nav;
    const filtered = nav.filter(item => item.section || !VIEWER_BLOCKED_PATHS.has(item.to));
    // Remove orphan section headers (a section followed immediately by another section or end of list)
    return filtered.filter((item, i) => {
      if (!item.section) return true;
      const next = filtered[i + 1];
      return next && !next.section;
    });
  })();
  return (
    <div className="sidebar">
      <div style={{ padding:'22px 20px 16px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:3 }}>
          <span style={{ fontSize:22 }}>⚡</span>
          <span style={{ fontSize:20,fontWeight:800,color:'white',letterSpacing:'-0.3px' }}>Pulse</span>
        </div>
        <div style={{ fontSize:11,color:'rgba(255,255,255,0.38)',letterSpacing:'0.3px' }}>Sales & Contracts Platform</div>
      </div>
      <nav className="sidebar-nav">
        {visibleNav.map((item, i) =>
          item.section ? (
            <div key={i} className="nav-section">{item.section}</div>
          ) : (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
              <span className="icon">{item.icon}</span>{item.label}
            </NavLink>
          )
        )}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{user?.name?.charAt(0)||'U'}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.name}</div>
            <div className="sidebar-user-role">{user?.role}{user?.team_name?` · ${user.team_name}`:''}</div>
          </div>
          <button className="logout-btn" onClick={logout} title="Logout">⎋</button>
        </div>
      </div>
    </div>
  );
}

function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem('pulse-dark') === 'true');
  useEffect(() => {
    document.body.classList.toggle('dark', dark);
    localStorage.setItem('pulse-dark', dark);
  }, [dark]);
  return [dark, setDark];
}

function TopBar() {
  const [dark, setDark] = useDarkMode();
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'var(--topbar-bg)',
      borderBottom: '1px solid var(--topbar-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 28px', height: 48,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div />
      <div style={{ display:'flex', alignItems:'center', gap:16 }}>
        <button
          className="dark-toggle"
          onClick={() => setDark(d => !d)}
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {dark ? '☀️' : '🌙'}
        </button>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', lineHeight:1.2 }}>
          <span style={{ fontSize:18, fontWeight:800, color:'var(--topbar-text)', letterSpacing:'-0.4px' }}>iShift.io</span>
          <span style={{ fontSize:10.5, color:'var(--topbar-subtext)', letterSpacing:'0.6px', textTransform:'uppercase', fontWeight:500 }}>Complexity Made Simple</span>
        </div>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  useEffect(() => {
    const saved = localStorage.getItem('pulse-dark') === 'true';
    document.body.classList.toggle('dark', saved);
  }, []);

  return (
    <div className="layout">
      <Sidebar />
      <div className="main-content">
        <TopBar />
        <div style={{ flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}
